/**
 * Enemy.js
 * The enemies basic class
 */

import { Game, GameTimer } from "pixi_core";

import { RandomSelector } from "../game/RandomSelector";
import { Common } from "../game/Common";
import { EnemyParticle } from "../game/particles/EnemyParticle";

import {
    Enemies,
    EnemyState,
    EnemyAbilities,
    GameEvents,
    shuffle,
    ENEMY_POSITIONS,
    ULTIMATE_ABILITIES,
    ENEMY_ATTACK_PARTICLE_TARGET_X,
    ENEMY_ATTACK_PARTICLE_TARGET_X_CAPTAIN_AMERICA,
    ENEMY_ATTACK_PARTICLE_TARGET_Y,
    ENEMY_ATTACK_PARTICLE_TARGET_Y_RANGE
} from "../Constants";

export class Enemy {
    /**
     * @public
     * @param {Enemies} enemyName
     * @param {Object} enemyData
     * @param {Number} index
     * @param {Boolean} [summoned] - Summoned by an ability
     */
    constructor(enemyName, enemyData, index, summoned = false) {
        /** @protected @type {Number} */
        this._index = index;

        /** @protected @type {{x: Number, y: Number}} */
        this._pos = ENEMY_POSITIONS[index];

        /** @protected @type {Number} */
        this._hp = enemyData.HP;

        /** @protected @type {Number} */
        this._maxHp = enemyData.HP;

        /** @protected @type {Number} */
        this._attackTimer = enemyData.AttackTimer;

        /** @protected @type {Number} */
        this._currentAttackTimer = 0;

        /** @protected @type {Number} */
        this._attackDamage = enemyData.AttackDamage;

        /** @protected @type {EnemyState} */
        this._enemyState = EnemyState.eUNDEFINED;

        /** @protected @type {Boolean} */
        this._preemptiveStrike = false;

        /** @protected @type {String[]} */
        this._weakness = enemyData.Weakness;

        /** @protected @type {String[]} */
        this._levelColors = enemyData.weaknessColors;

        /** @protected @type {Boolean} */
        this._firstStrike = enemyData.FirstStrike === true;

        /** @protected @type {Boolean} */
        this._isABoss = enemyData.Boss === true;

        /** @protected @type {Boolean} */
        this._hasVariableWeaknesses = enemyData.VariableWeakness === true;

        /** @protected @type {Number} */
        this._nbVariableWeaknesses = Game.utils.assertNumber(enemyData.NbWeakness, 1);

        /** @protected @type {Number} - Number bubbles changed by the Hacker/Telekinesis abilities */
        this._colorChangeBubbles = Game.utils.assertNumber(enemyData.ColorChangeBubbles, 1);

        /** @protected @type {Boolean} */
        this._isShielded = false;

        /** @protected @type {Number} */
        this._shieldCounter = 0;

        /** @protected @type {Enemies} */
        this._enemyName = enemyName;

        /** @protected @type {Number} */
        this._damagedTimer = 0;

        /** @protected @type {Number} */
        this._damagedReceivedMultiplier = 1;

        /** @protected @type {Number} */
        this._damagedDuration = enemyData.damagedTimer;

        if (!this._firstStrike || summoned === true) {
            this._currentAttackTimer = this._attackTimer;
        }

        /** @protected @type {Boolean} */
        this._cheatEnemyAlwaysAttack = false;

        /** @protected @type {Number} - Number of actions per attack turn */
        this._nbActionsPerTurn = Game.utils.assertNumber(enemyData.NbActions, 1);

        /** @protected @type {Number} - Number of performed in the current attack phase */
        this._nbActionsPerformed = 0;

        /** @protected @type {RandomSelector} - Ability choices with their probabilities */
        this._abilityChoices = new RandomSelector();

        /** @protected @type {EnemyAbilities} - Selected ability for the next attack phase action */
        this._selectedAbility = EnemyAbilities.eNONE;

        // TODO: Verify is multiple action delay is still in use

        /** @protected @type {Number} */
        this._multipleActionDelay = enemyData.multipleActionDelay;

        /** @protected @type {GameTimer} */
        this._timerMultipleActionDelay = null;

        /** @protected @type {GameTimer} */
        this._timerMultipleAbilityDelay = null;

        // Ability values that can be shared by multiple enemies.  Will be used only if the enemy has the corresponding abilities.

        /** @protected @type {Number} - Number of bubbles to cast with the AddRandom ability */
        this._nbBubblesAdd = Game.utils.assertNumber(enemyData.NbBubblesAdd, 1);

        /** @protected @type {Number} - Number of bubbles to cast with the AddRandom ability */
        this._chargingDamageBonus = Game.utils.assertNumber(enemyData.ChargingDamageBonus, 100);

        /** @protected @type {Number} - Number of bubbles to cast with the Ultron Control ability */
        this._addOvertimeBubbles = Game.utils.assertNumber(enemyData.AddOvertimeBubbles, 1);

        /** @protected @type {Number} - Number of Avengers stunned by the Concussive Beam ability */
        this._stunNumber = Game.utils.assertNumber(enemyData.StunNbr, 1);

        /** @protected @type {Number} - Number of turns an Avenger is stunned by the Concussive Beam ability */
        this._stunTurns = Game.utils.assertNumber(enemyData.StunTurns, 1);

        /** @protected @type {Number} - Number of bubbles to cast with the Illusion ability */
        this._nbBubblesDecoy = Game.utils.assertNumber(enemyData.DecoyNbr, 1);

        /** @protected @type {Number} - Number of lines added with abilities that do */
        this._nbAddedLines = Game.utils.assertNumber(enemyData.NbAddedLines, 1);

        /** @protected @type {Number} - Number of shielded enemies */
        this._shieldNbr = Game.utils.assertNumber(enemyData.ShieldNbr, 1);

        /** @protected @type {Number} - Shield duration in turns */
        this._shieldDuration = Game.utils.assertNumber(enemyData.ShieldDuration, 1);

        /** @protected @type {Number} - Shield percentage */
        this._shieldPercent = Game.utils.assertNumber(enemyData.ShieldPercent, 1);

        /** @protected @type {Number} - Percentage of health to activate an ability that requires an HP threshold */
        this._triggerHealthPercent = Game.utils.assertNumber(enemyData.TriggerHealthPercent, 1);

        /** @protected @type {Number} - Number of bubbles randomly added to the shooter */
        this._randomShooterBubbles = Game.utils.assertNumber(enemyData.RandomShooterBubbles, 1);

        /** @public @type {Number} */
        this.blackWidowStunTimer = 0;

        // End of common ability values

        this.setVariableWeaknesses();

        Game.on(GameEvents.eATTACKPHASE_FLASH_COMPLETED, this._onFlashCompleted, this);
        Game.on(GameEvents.eATTACKPHASE_NEXT_ACTION, this._onNextAction, this);
    }

    /**
     * @private
     * @param {EnemyState} state
     */
    _setState(state) {
        if (this._enemyState === state) {
            return;
        }

        this._enemyState = state;

        switch (this._enemyState) {
            case EnemyState.eIDLE:
                this._initStateIdle();
                break;
            case EnemyState.eATTACKING:
                this._initStateAttacking();
                break;
            case EnemyState.eDAMAGED:
                this._initStateDamaged();
                break;
            case EnemyState.eDYING:
                this._initStateDying();
                break;
            case EnemyState.eDONE:
                this._initStateDone();
                break;
        }
    }

    /**
     * @public
     * @param {Number} deltaTime
     */
    updateLoop(deltaTime) {
        this._updateState(deltaTime);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateState(deltaTime) {
        switch (this._enemyState) {
            case EnemyState.eIDLE:
                this._updateStateIdle(deltaTime);
                break;
            case EnemyState.eATTACKING:
                this._updateStateAttacking(deltaTime);
                break;
            case EnemyState.eDAMAGED:
                this._updateStateDamaged(deltaTime);
                break;
            case EnemyState.eDYING:
                this._updateStateDying(deltaTime);
                break;
            case EnemyState.eDONE:
                this._updateStateDone(deltaTime);
                break;
        }
    }

    /**
     * @private
     */
    _initStateIdle() {}

    /**
     * @protected
     */
    _initStateAttacking() {
        this._nbActionsPerformed = 0;
        this._selectedAbility = EnemyAbilities.eNONE;

        Game.fire(GameEvents.eATTACKPHASE_FLASH_BEGIN, this);
    }

    /**
     * @public
     */
    setVariableWeaknesses() {
        if (this._hasVariableWeaknesses) {
            const randomWeakness = shuffle(this._levelColors);
            this._weakness = [];

            for (let i = 0; i < this._nbVariableWeaknesses; ++i) {
                this._weakness.push(randomWeakness[i]);
            }
        }
    }

    /**
     * @public
     * @param {String[]} previousWeakness
     */
    setNewVariableWeaknesses(previousWeakness) {
        if (previousWeakness.length > this._nbVariableWeaknesses) {
            previousWeakness = [];
        }
        const randomWeakness = shuffle(this._levelColors);
        this._weakness = [];

        let isPreviousWeakness = false;
        for (let i = 0; i < randomWeakness.length; ++i) {
            isPreviousWeakness = false;
            for (let j = 0; j < previousWeakness.length; ++j) {
                if (randomWeakness[i] === previousWeakness[j]) {
                    isPreviousWeakness = true;
                }
            }
            if (!isPreviousWeakness) {
                this._weakness.push(randomWeakness[i]);
            }

            if (this._weakness.length >= this._nbVariableWeaknesses) {
                break;
            }
        }
    }

    /**
     * @private
     * @param {Enemy} attacker
     */
    _onFlashCompleted(attacker) {
        if (attacker.index === this._index) {
            if (this._preemptiveStrike) {
                Game.fire(GameEvents.eATTACKPHASE_ABILITY_BEGIN, this, EnemyAbilities.ePREEMPTIVE_STRIKE);
            } else {
                this._chooseNextAction();
            }
        }
    }

    /**
     * Choose the next action in this enemy's attack phase
     * @protected
     */
    _chooseNextAction() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn && this._hp > 0) {
            ++this._nbActionsPerformed;

            if (this._currentAttackTimer > 0) {
                this._regularAttack();
            } else {
                this._beginAbility(this._chooseAbility());
            }
        } else {
            this._onAttackPhaseCompleted();
        }
    }

    /**
     * Choose the next ability to use
     * Override in specialized class if special conditions are needed
     * @protected
     * @returns {EnemyAbilities}
     */
    _chooseAbility() {
        return this._abilityChoices.choose();
    }

    /**
     * Performs the regular/normal attack on heroes
     * @protected
     */
    _regularAttack() {
        Game.fire(GameEvents.eATTACKPHASE_ATTACK_BEGIN, this);
    }

    /**
     * The enemy slide-in animation is at the far left
     * @public
     * @param {Number} startX
     * @param {Number} startY
     */
    fireDamageParticle(startX, startY) {
        let targetX = ENEMY_ATTACK_PARTICLE_TARGET_X;
        let targetY =
            ENEMY_ATTACK_PARTICLE_TARGET_Y -
            ENEMY_ATTACK_PARTICLE_TARGET_Y_RANGE +
            ENEMY_ATTACK_PARTICLE_TARGET_Y_RANGE * Math.random() * 2;

        if (Common.heroes.isCaptainAmericaShieldActive()) {
            targetX = ENEMY_ATTACK_PARTICLE_TARGET_X_CAPTAIN_AMERICA;
        }

        Common.particles.createEnemyAttackParticle(startX, startY, targetX, targetY, this._onDamageParticleCompleted.bind(this));
    }

    /**
     * The attack particle has reached its target
     * @protected
     * @param {EnemyParticle} particle
     */
    _onDamageParticleCompleted(particle) {
        Game.fire(GameEvents.eHEROES_HIT, this._attackDamage, particle);
    }

    /**
     * @protected
     * @param {EnemyAbilities} abilityName
     */
    _beginAbility(abilityName) {
        let isUltimateAbility = false;

        for (let i = 0; i < ULTIMATE_ABILITIES.length; ++i) {
            if (abilityName === ULTIMATE_ABILITIES[i]) {
                isUltimateAbility = true;
                break;
            }
        }

        this._selectedAbility = abilityName;
        if (this._isABoss && isUltimateAbility) {
            Game.fire(GameEvents.eATTACKPHASE_CUTIN_BEGIN, this._selectedAbility);
        } else {
            Game.fire(GameEvents.eATTACKPHASE_ABILITY_BEGIN, this, abilityName);
        }
    }

    /**
     * The ability name is now hidden
     * @public
     * @param {{x: Number, y: Number}} startPos
     */
    onAbilityNameCompleted(startPos) {
        if (this._preemptiveStrike) {
            this._preemptiveStrike = false;
            this._chooseNextAction();
        } else {
            this._useAbility(this._selectedAbility, startPos);
        }
    }

    /**
     * Overriden in specialized enemy class
     * @protected
     * @param {EnemyAbilities} abilityName
     * @param {{x: Number, y: Number}} startPos
     */
    _useAbility(abilityName, startPos) {}

    /**
     * @protected
     */
    _onAbilityCompleted() {
        if (this._nbActionsPerformed < this._nbActionsPerTurn) {
            if (this._timerMultipleActionDelay) {
                this._timerMultipleActionDelay.destroy();
                this._timerMultipleActionDelay = null;
            }

            this._timerMultipleActionDelay = Game.windows.getCurrentWindow().createTimer(this._multipleActionDelay);
            this._timerMultipleActionDelay.onComplete = () => {
                this._chooseNextAction();
            };
        } else {
            this._chooseNextAction();
        }
    }

    /**
     * This enemy's attack phase is completed
     * @protected
     */
    _onAttackPhaseCompleted() {
        if (this._currentAttackTimer <= 0) {
            this.resetAttackTimer();
        }

        Game.fire(GameEvents.eGAME_SET_ENEMY_ATTACK_TIMER, this._index, this.currentAttackTimer, false);

        Game.fire(GameEvents.eATTACKPHASE_ENEMY_ATTACK_COMPLETED, this);

        if (this._hp > 0) {
            this._setState(EnemyState.eIDLE);
        }
    }

    /**
     * Callback to handle animation timing of basic attacks
     * @private
     * @param {Enemy} attacker
     */
    _onNextAction(attacker) {
        if (attacker.index === this._index) {
            if (this._nbActionsPerformed < this._nbActionsPerTurn) {
                if (this._timerMultipleActionDelay) {
                    this._timerMultipleActionDelay.destroy();
                    this._timerMultipleActionDelay = null;
                }

                this._timerMultipleActionDelay = Game.windows.getCurrentWindow().createTimer(this._multipleActionDelay);
                this._timerMultipleActionDelay.onComplete = () => {
                    this._chooseNextAction();
                };
            } else {
                this._chooseNextAction();
            }
        }
    }

    /**
     * @private
     */
    _initStateDamaged() {
        this._damagedTimer = 0;
    }

    /**
     * @private
     */
    _initStateDying() {}

    /**
     * @private
     */
    _initStateDone() {}

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateIdle(deltaTime) {}

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateAttacking(deltaTime) {}

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDamaged(deltaTime) {
        this._damagedTimer += deltaTime;

        if (this._damagedTimer >= this._damagedDuration) {
            if (this._hp <= 0) {
                this._setState(EnemyState.eDYING);
            } else {
                this._setState(EnemyState.eIDLE);
            }
        }
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDying(deltaTime) {
        this._setState(EnemyState.eDONE);
    }

    /**
     * @private
     * @param {Number} deltaTime
     */
    _updateStateDone(deltaTime) {}

    /**
     * @public
     * @param {Number} nbTurns
     * @param {Number} percentageShielding
     */
    setShield(nbTurns, percentageShielding) {
        this._isShielded = true;
        this._damagedReceivedMultiplier = 1 - percentageShielding / 100;
        this._shieldCounter = nbTurns;
    }

    /**
     * @public
     * @param {Boolean} preemptiveStrike
     */
    attack(preemptiveStrike) {
        this._preemptiveStrike = preemptiveStrike;

        if ((this._preemptiveStrike && !this._firstStrike) || this.blackWidowStunTimer > 0) {
            this._onAttackPhaseCompleted();

            if (this.blackWidowStunTimer > 0) {
                Game.fire(GameEvents.eGAME_STUN_ENEMY, this);
                --this.blackWidowStunTimer;
            }
        } else {
            this._setState(EnemyState.eATTACKING);
        }
    }

    /**
     * @public
     * @param {Number} damage
     */
    attacked(damage) {
        this._tickDownShield();
        if (damage <= 0) return;

        this._hp -= damage;

        if (this._hp < 0) {
            this._hp = 0;
        }

        this._setState(EnemyState.eDAMAGED);
    }

    /**
     * @private
     */
    _tickDownShield() {
        if (this._isShielded) {
            --this._shieldCounter;
            Game.fire(GameEvents.eGAME_SET_ENEMY_SHIELD_COUNTER, this._shieldCounter, this);

            if (this._shieldCounter <= 0) {
                this.removeShield();
            }
        }
    }

    /**
     * @public
     * @param {Number} health
     */
    heal(health) {
        this._hp += health;

        if (this._hp > this._maxHp) {
            this._hp = this._maxHp;
        }
    }

    /**
     * @public
     */
    resetAttackTimer() {
        this._currentAttackTimer = this._cheatEnemyAlwaysAttack ? 1 : this._attackTimer;
    }

    /**
     * @public
     */
    lowerAttackTimer() {
        if (this._hp <= 0) {
            return;
        }

        this._currentAttackTimer -= 1;

        if (this._currentAttackTimer <= 0) {
            this._currentAttackTimer = 0;
        }
    }

    /**
     * @public
     */
    setBlackWidowStun(stunTimer) {
        if (this._hp <= 0) {
            return;
        }

        this.blackWidowStunTimer = stunTimer;
    }

    /**
     * @public
     */
    removeShield() {
        this._isShielded = false;
        this._damagedReceivedMultiplier = 1;
        this._shieldCounter = 0;
    }

    /**
     * @public
     * @param {Boolean} enabled
     */
    cheatsEnemyAlwaysAttack(enabled) {
        this._cheatEnemyAlwaysAttack = enabled;

        if (this._cheatEnemyAlwaysAttack) {
            if (this._currentAttackTimer > 0) {
                this._currentAttackTimer = 1;
            }
        }
    }

    /**
     * Returns the enemy's name
     * @public
     * @returns {String}
     */
    get name() {
        return this._enemyName;
    }

    /**
     * @public
     * @returns {{x: Number, y: Number}}
     */
    get pos() {
        return this._pos;
    }

    /**
     * @public
     * @returns {Number}
     */
    get x() {
        return this._pos.x;
    }

    /**
     * @public
     * @returns {Number}
     */
    get y() {
        return this._pos.y;
    }

    /**
     * Returns the enemy's hp
     * @public
     * @returns {Number}
     */
    get hp() {
        return this._hp;
    }

    /**
     * Returns the enemy's Max HP
     * @public
     * @returns {Number}
     */
    get maxHp() {
        return this._maxHp;
    }

    /**
     * Is the enemy alive?
     * @public
     * @returns {Boolean}
     */
    get isAlive() {
        return this._hp > 0;
    }

    /** Returns the enemy's current attack Timer
     * @public
     * @returns {Number}
     */
    get currentAttackTimer() {
        return this._currentAttackTimer;
    }

    /**
     * Returns the enemy's state
     * @public
     * @returns {EnemyState}
     */
    get enemyState() {
        return this._enemyState;
    }

    /**
     * Returns the enemy's weaknesses
     * @public
     * @returns {String[]}
     */
    get weakness() {
        return this._weakness;
    }

    /**
     * Returns the enemy's variable weakness status
     * @public
     * @returns {Boolean}
     */
    get variableWeakness() {
        return this._hasVariableWeaknesses;
    }

    /**
     * Returns the enemy's variable weakness status
     * @public
     * @returns {Number}
     */
    get nbVariableWeakness() {
        return this._nbVariableWeaknesses;
    }

    /**
     * Returns if the enemy will do a preemptive strike
     * @public
     * @returns {Boolean}
     */
    get firstStrike() {
        return this._firstStrike;
    }

    /**
     * Returns if the enemy is a boss
     * @public
     * @returns {Boolean}
     */
    get isABoss() {
        return this._isABoss;
    }

    /**
     * Returns if the enemy has an active shield
     * @public
     * @returns {Boolean}
     */
    get isShielded() {
        return this._isShielded;
    }

    /**
     * Returns the multiplier to damage while a shield is active on the enemy.
     * @public
     * @returns {Number}
     */
    get shieldDamageMultiplier() {
        return this._damagedReceivedMultiplier;
    }

    /**
     * @public
     * @returns {Number}
     */
    get index() {
        return this._index;
    }

    /**
     * @public
     */
    destroy() {
        if (this._timerMultipleActionDelay) {
            this._timerMultipleActionDelay.destroy();
            this._timerMultipleActionDelay = null;
        }

        if (this._timerMultipleAbilityDelay) {
            this._timerMultipleAbilityDelay.destroy();
            this._timerMultipleAbilityDelay = null;
        }

        this._abilityChoices.destroy();
        this._abilityChoices = null;

        Game.off(GameEvents.eATTACKPHASE_FLASH_COMPLETED, this._onFlashCompleted, this);
        Game.off(GameEvents.eATTACKPHASE_NEXT_ACTION, this._onNextAction, this);
    }
}
